<template>
    <div :class="[{'mb-2': !hasTree}]">
        <div class="relative">
            <input type="text"
                   v-model.trim="search"
                   @input="goSearch"
                   @focus="searchFocus = true"
                   @blur="searchFocus = false"
                   placeholder="Search"
                   autocomplete="nope"
                   :ref="`autocomplete-input-${item.code}`"
                   class="form-control pr-[28px]"
                   :class="{'focused': showResult}">

            <svg v-if="search"
                 @click="clearSearch"
                 height="14"
                 width="14"
                 viewBox="0 0 512 512"
                 class="fill-current transition-all cursor-pointer absolute right-[8px] top-[16px] text-gray-400 hover:text-red-400">
                <path
                    d="M437.5,386.6L306.9,256l130.6-130.6c14.1-14.1,14.1-36.8,0-50.9c-14.1-14.1-36.8-14.1-50.9,0L256,205.1L125.4,74.5  c-14.1-14.1-36.8-14.1-50.9,0c-14.1,14.1-14.1,36.8,0,50.9L205.1,256L74.5,386.6c-14.1,14.1-14.1,36.8,0,50.9  c14.1,14.1,36.8,14.1,50.9,0L256,306.9l130.6,130.6c14.1,14.1,36.8,14.1,50.9,0C451.5,423.4,451.5,400.6,437.5,386.6z"></path>
            </svg>

            <template v-if="searchFocus && search.length < 2 && !searchNotFound">
                <div
                    class="p-2 text-[12px] leading-snug absolute top-[calc(100%+.5rem)] w-full z-10 border bg-white shadow-md rounded-md">
                    {{ item.config.text_search_info }}
                </div>
            </template>

            <template v-if="searchNotFound">
                <div class="absolute top-[calc(100%+.5rem)] w-full z-10 bg-white shadow-md rounded-md overflow-hidden">
                    <div
                        class="p-2 text-[12px] leading-snug bg-danger bg-opacity-20">
                        {{ item.config.text_search_not_found }}
                    </div>
                </div>
            </template>
        </div>

        <div class="mt-2 relative"
             :class="[{'border bg-white shadow-md rounded-md': !majorGroup}]"
             v-if="showResult || majorGroup">
            <div class="relative"
                 v-if="(!searchNotFound && showTree) || selected.length || majorGroup">
                <div
                    class="absolute bg-white bg-opacity-50 w-full h-full flex items-center justify-center z-10 left-0 top-0"
                    :class="{'hidden': !loading}">
                    <spinner/>
                </div>

                <div class="border-b p-2" v-if="hasTree && showTree && !majorGroup">
                    <label class="checkbox-ui">
                        <input type="checkbox"
                               :disabled="loading"
                               v-model="treeMode"/>
                        <span></span>
                        Show 2-4 digit codes
                    </label>
                </div>

                <div class="p-2 flex items-start flex-wrap max-h-[250px] overflow-y-auto"
                     :class="{'border-b': !majorGroup}"
                     v-if="selected.length">
                    <template v-for="item in selected.slice(0, countShowSelected)">
                        <div
                            class="flex rounded-xl border py-1 px-2 bg-yellow-100 mb-1 mr-1 relative pr-[24px] leading-snug"
                            :key="item.id">
                            {{ item.title }}

                            <svg @click="removeItem(item)"
                                 height="14"
                                 width="14"
                                 viewBox="0 0 512 512"
                                 class="fill-current transition-all cursor-pointer cursor-pointer ml-2 absolute right-[5px] top-[8px] text-primary">
                                <path
                                    d="M437.5,386.6L306.9,256l130.6-130.6c14.1-14.1,14.1-36.8,0-50.9c-14.1-14.1-36.8-14.1-50.9,0L256,205.1L125.4,74.5  c-14.1-14.1-36.8-14.1-50.9,0c-14.1,14.1-14.1,36.8,0,50.9L205.1,256L74.5,386.6c-14.1,14.1-14.1,36.8,0,50.9  c14.1,14.1,36.8,14.1,50.9,0L256,306.9l130.6,130.6c14.1,14.1,36.8,14.1,50.9,0C451.5,423.4,451.5,400.6,437.5,386.6z"></path>
                            </svg>
                        </div>
                    </template>

                    <span v-if="selected.length > limitShowSelected"
                          class="cursor-pointer text-primary font-semibold hover:underline ml-2 mt-[4px]"
                          @click="toggleAllSelected()">
                        {{ countShowSelected > limitShowSelected ? "See less" : "See more" }}
                    </span>
                </div>

                <div class="p-2 min-h-[70px] overflow-y-auto relative"
                     :class="[{'border-b max-h-[350px]': !majorGroup}, {'max-h-[500px]': majorGroup}]"
                     v-if="formatValues.length || loading || asyncTree">
                    <template v-if="asyncTree">
                        <v-jstree :data="formatValues"
                                  :async="loadData"
                                  :id="'mark-highlighter'"
                                  ref="major-groups"
                                  whole-row
                                  allow-batch
                                  show-checkbox
                                  multiple
                                  text-field-name="title"
                                  @item-click="itemClick"/>
                    </template>
                    <template v-else>
                        <v-jstree :data="formatValues"
                                  :class="[{'one-level-mode': !treeMode}, {'only-number-search': onlyNumber}]"
                                  :id="'mark-highlighter'"
                                  whole-row
                                  allow-batch
                                  show-checkbox
                                  multiple
                                  text-field-name="title"
                                  @item-click="itemClick"/>
                    </template>
                </div>

                <div :class="[{'p-2': !majorGroup}, {'mt-2': majorGroup}]">
                    <button class="btn btn-blue bg-fully w-full uppercase"
                            :disabled="disabledSave"
                            @click="save">
                        Save
                    </button>
                </div>
            </div>
        </div>

        <template v-if="!majorGroup && hasTree">
            <div @click="showMajorGroup"
                 class="mt-1 cursor-pointer inline-flex items-center underline text-primary hover:no-underline">
                Major Industry Group
                <svg width="16"
                     height="16"
                     viewBox="0 0 16 16"
                     fill="none"
                     class="ml-2">
                    <path
                        d="M0 14.1778V12.8444H5.48889V14.1778H0ZM0 3.15556V1.82222H9.17778V3.15556H0ZM6.82222 16V11H8.15556V12.8444H16V14.1778H8.15556V16H6.82222ZM4.15556 10.4889V8.66667H0V7.33333H4.15556V5.46667H5.48889V10.4889H4.15556ZM6.82222 8.66667V7.33333H16V8.66667H6.82222ZM10.5111 5V0H11.8444V1.82222H16V3.15556H11.8444V5H10.5111Z"
                        fill="#039FFF"/>
                </svg>
            </div>
        </template>
    </div>
</template>

<script>
    import debounce from "lodash/debounce";
    import VJstree from "vue-jstree";
    import Mark from "mark.js/dist/mark.min";
    import { EventBus } from "@widget/plugins/event-bus";
    import { v4 as uuidv4 } from "uuid";

    export default {
        name: "autocomplete-tree",
        components: {
            VJstree
        },
        props: {
            item: {
                type: Object,
                require: true,
                default: () => {
                    return {};
                }
            },
            preSelected: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            majorGroup: {
                type: Boolean,
                default: false
            }
        },
        watch: {
            search: debounce(function () {
                if (this.showTree) {
                    this.getTree(true);
                }

                if (!this.search && this.majorGroup) {
                    this.refreshMajor();
                }
            }, 500),
            treeMode () {
                if (this.majorGroup) return;

                this.formatValues = [];
                if (this.values.length) {
                    this.reformatValues();
                    return;
                }
                this.loading = true;
                this.getTree();
            },
            storeSelected () {
                this.checkStoreSelect();
            }
        },
        computed: {
            values () {
                return this.$store.getters["widgetData/getSelectionsByCode"](this.item.code, this.treeMode);
            },
            showTree () {
                return this.search.length >= 2;
            },
            hasTree () {
                return this.item.config.has_tree ?? false;
            },
            onlyNumber () {
                return /^\d+$/.test(this.search);
            },
            showResult () {
                return this.showTree || this.values.length || this.selected.length;
            },
            searchNotFound () {
                return this.showTree && !this.loading && !this.formatValues.length;
            },
            disabledSave () {
                return !this.selected.length || this.loading;
            },
            storeSelected () {
                return this.$store.getters["widgetData/getSelectByCode"](this.item.code);
            },
            asyncTree () {
                return this.majorGroup && !this.search;
            }
        },
        methods: {
            checkStoreSelect () {
                if (this.storeSelected) {
                    this.$store.commit("widgetData/setValuesSelect", {
                        code: this.item.code,
                        key: "values",
                        values: []
                    });

                    this.$store.commit("widgetData/setValuesSelect", {
                        code: this.item.code,
                        key: "valuesGrouped",
                        values: []
                    });

                    this.formatValues = [];
                    this.search = "";
                    this.searchFocus = false;

                    this.selected = this.storeSelected.query.map((item, index) => ({
                        id: uuidv4(),
                        value: item,
                        title: this.storeSelected.queryUI[index]
                    }));

                    this.$emit("open");
                }
            },
            clearSearch () {
                this.loading = true;
                this.search = "";
                this.$store.commit("widgetData/setValuesSelect", {
                    code: this.item.code,
                    key: "values",
                    values: []
                });
                this.$store.commit("widgetData/setValuesSelect", {
                    code: this.item.code,
                    key: "valuesGrouped",
                    values: []
                });
                if (!this.majorGroup) {
                    this.loading = false;
                }
            },
            goSearch () {
                if (!this.showTree) {
                    this.$store.commit("widgetData/setValuesSelect", {
                        code: this.item.code,
                        key: "values",
                        values: []
                    });
                    this.$store.commit("widgetData/setValuesSelect", {
                        code: this.item.code,
                        key: "valuesGrouped",
                        values: []
                    });
                    this.loading = false;
                } else {
                    this.loading = true;
                }
            },
            itemClick (node, item) {
                if (this.treeMode) {
                    let parent_2 = this.checkParent(this.formatValues, item.value);
                    if (parent_2 && parent_2.value !== item.value) {
                        parent_2.selected = !parent_2.children.find(child => {
                            return !child.selected && !child.loading;
                        });

                        let parent_4 = this.checkParent(this.formatValues, parent_2.value);
                        if (parent_4 && parent_4.value !== parent_2.value) {
                            parent_4.selected = !parent_4.children.find(child => {
                                return !child.selected && !child.loading;
                            });

                            let parent_6 = this.checkParent(this.formatValues, parent_4.value);
                            if (parent_6 && parent_6.value !== parent_4.value) {
                                parent_6.selected = !parent_6.children.find(child => {
                                    return !child.selected && !child.loading;
                                });
                            }
                        }
                    }

                    this.treeSelected = [];

                    this.formatValues.forEach(child_2 => {
                        if (child_2.selected) {
                            this.addItem(child_2);
                        } else {
                            if (child_2.children) {
                                child_2.children.forEach(child_4 => {
                                    if (child_4.selected) {
                                        this.addItem(child_4);
                                    } else {
                                        if (child_4.children) {
                                            child_4.children.forEach(child_6 => {
                                                if (child_6.selected) {
                                                    this.addItem(child_6);
                                                } else {
                                                    if (child_6.children) {
                                                        child_6.children.forEach(child_8 => {
                                                            if (child_8.selected) {
                                                                this.addItem(child_8);
                                                            }
                                                        });
                                                    }
                                                }
                                            });
                                        }
                                    }
                                });
                            }
                        }
                    });

                    this.formatValues.forEach(child_2 => {
                        let index = this.selected.findIndex(itemChild => itemChild.value === child_2.value);
                        if (index !== -1) {
                            this.$delete(this.selected, index);
                        }
                        if (child_2.children) {
                            child_2.children.forEach(child_4 => {
                                let index = this.selected.findIndex(itemChild => itemChild.value === child_4.value);
                                if (index !== -1) {
                                    this.$delete(this.selected, index);
                                }

                                if (child_4.children) {
                                    child_4.children.forEach(child_6 => {
                                        let index = this.selected.findIndex(itemChild => itemChild.value === child_6.value);
                                        if (index !== -1) {
                                            this.$delete(this.selected, index);
                                        }

                                        if (child_6.children) {
                                            child_6.children.forEach(child_8 => {
                                                let index = this.selected.findIndex(itemChild => itemChild.value === child_8.value);
                                                if (index !== -1) {
                                                    this.$delete(this.selected, index);
                                                }
                                            });
                                        }
                                    });
                                }
                            });
                        }
                    });

                    this.selected.push(...this.treeSelected);

                    this.countShowSelected = this.selected.length;
                } else {
                    if (item.selected) {
                        this.addItem(item);
                    } else {
                        this.removeItem(item);
                    }
                }
            },
            addItem (model) {
                if (this.treeMode) {
                    this.treeSelected.push({
                        value: model.value,
                        title: model.title
                    });
                } else {
                    let index = this.selected.findIndex(itemChild => itemChild.value === model.value);
                    if (index === -1) {
                        this.selected.push({
                            value: model.value,
                            title: model.title
                        });

                        this.countShowSelected = this.selected.length;
                    }
                }
            },
            removeItem (model) {
                if (this.treeMode) {
                    this.formatValues.forEach(child_2 => {
                        if (child_2.value === model.value) {
                            child_2.selected = false;

                            child_2.children.forEach(child_4 => {
                                child_4.selected = false;
                                if (child_4.children) {
                                    child_4.children.forEach(child_6 => {
                                        child_6.selected = false;
                                        if (child_6.children) {
                                            child_6.children.forEach(child_8 => {
                                                child_8.selected = false;
                                            });
                                        }
                                    });
                                }
                            });
                        } else {
                            if (child_2.children) {
                                child_2.children.forEach(child_4 => {
                                    if (child_4.value === model.value) {
                                        child_4.selected = false;

                                        if (child_4.children) {
                                            child_4.children.forEach(child_6 => {
                                                child_6.selected = false;

                                                if (child_6.children) {
                                                    child_6.children.forEach(child_8 => {
                                                        child_8.selected = false;
                                                    });
                                                }
                                            });
                                        }
                                    } else {
                                        if (child_4.children) {
                                            child_4.children.forEach(child_6 => {
                                                if (child_6.value === model.value) {
                                                    child_6.selected = false;

                                                    if (child_6.children) {
                                                        child_6.children.forEach(child_8 => {
                                                            child_8.selected = false;
                                                        });
                                                    }
                                                }
                                            });
                                        }
                                    }
                                });
                            }
                        }
                    });

                    let parent = this.checkParent(this.formatValues, model.value);
                    if (parent) {
                        parent.selected = !parent.children.find(child => !child.selected);

                        let parentIndex = this.selected.findIndex(itemChild => itemChild.value === parent.value);
                        if (parentIndex !== -1) {
                            this.$delete(this.selected, parentIndex);
                        }

                        let topParent = this.checkParent(this.formatValues, parent.value);
                        if (topParent) {
                            topParent.selected = !topParent.children.find(child => !child.selected);

                            let topParentIndex = this.selected.findIndex(itemChild => itemChild.value === topParent.value);
                            if (topParentIndex !== -1) {
                                this.$delete(this.selected, topParentIndex);
                            }
                        }
                    }

                    let index = this.selected.findIndex(itemChild => itemChild.value === model.value);
                    if (index !== -1) {
                        this.$delete(this.selected, index);
                    }
                } else {
                    let indexData = this.formatValues.findIndex(itemChild => itemChild.value === model.value);
                    if (indexData !== -1) {
                        this.$set(this.formatValues[indexData], "selected", false);
                    }

                    let index = this.selected.findIndex(itemChild => itemChild.value === model.value);
                    if (index !== -1) {
                        this.$delete(this.selected, index);
                    }
                }
            },
            getTree (search = false) {
                let params = {
                    per_page: this.perPage
                };

                if (this.search) {
                    params.query = this.search;
                }

                if (this.treeMode) {
                    params.grouped = 1;
                }

                if (this.majorGroup) {
                    params.major = 1;
                    if (!this.search) {
                        params.level = 1;
                    }
                }

                if (search) {
                    if (this.treeMode) {
                        this.$store.commit("widgetData/setValuesSelect", {
                            code: this.item.code,
                            key: "values",
                            values: []
                        });
                    } else {
                        this.$store.commit("widgetData/setValuesSelect", {
                            code: this.item.code,
                            key: "valuesGrouped",
                            values: []
                        });
                    }
                }

                this.$store.dispatch("widgetData/getTree", {
                    code: this.item.code,
                    params
                }).finally(() => {
                    setTimeout(() => {
                        this.reformatValues();
                        this.loading = false;
                    }, 500);
                });
            },
            marked () {
                setTimeout(() => {
                    if (this.mark) {
                        this.mark.unmark({
                            done: () => {
                                this.mark.mark(this.search.split(","));
                            }
                        });
                    }
                }, 0);
            },
            save () {
                let titles = this.selected.map(item => item.title);

                this.$store.commit("widgetData/addSelect", {
                    sensitive: this.item.attentionText !== "",
                    code: this.item.code,
                    group: this.item.group,
                    title: `${this.item.title}: ${titles.slice(0, 5).join(", ")}`,
                    showMore: titles.length > 5,
                    queryUI: titles,
                    query: this.selected.map(item => item.value)
                });

                this.$emit("saved");

                this.clearSearch();
            },
            reformatValues () {
                if (this.asyncTree) {
                    this.formatValues = this.values.map(child => {
                        let selected = this.selected.find(item => child.value === item.value);
                        return {
                            ...child,
                            opened: false,
                            loading: false,
                            isLeaf: true,
                            selected: !!selected,
                            level: 1,
                            children: [
                                {
                                    title: "Loading...",
                                    value: "Loading...",
                                    opened: false,
                                    selected: false,
                                    disabled: true,
                                    loading: true,
                                    children: []
                                }
                            ]
                        };
                    });
                } else {
                    this.formatValues = this.values.map(child_2 => {
                        let selected_2 = !!this.selected.find(item => child_2.value === item.value);
                        return {
                            ...child_2,
                            children: child_2.children ? child_2.children.map(child_4 => {
                                let selected_4 = !!this.selected.find(item => child_4.value === item.value) || selected_2;
                                return {
                                    ...child_4,
                                    children: child_4.children ? child_4.children.map(child_6 => {
                                        let selected_6 = !!this.selected.find(item => child_6.value === item.value) || selected_4;
                                        return {
                                            ...child_6,
                                            children: child_6.children ? child_6.children.map(child_8 => {
                                                let selected_8 = !!this.selected.find(item => child_8.value === item.value) || selected_6;
                                                return {
                                                    ...child_8,
                                                    selected: selected_8
                                                };
                                            }) : [],
                                            opened: true,
                                            selected: selected_6
                                        };
                                    }) : [],
                                    opened: true,
                                    selected: selected_4
                                };
                            }) : [],
                            opened: true,
                            selected: selected_2
                        };
                    });
                }
                this.marked();
            },
            checkParent (array, value) {
                let parent = null;
                array.forEach(child_2 => {
                    if (parent) return;
                    if (child_2.value === value) {
                        parent = child_2;
                    } else {
                        if (child_2.children) {
                            child_2.children.forEach(child_4 => {
                                if (parent) return;
                                if (child_4.value === value) {
                                    parent = child_2;
                                } else {
                                    if (child_4.children) {
                                        child_4.children.forEach(child_6 => {
                                            if (parent) return;
                                            if (child_6.value === value) {
                                                parent = child_4;
                                            } else {
                                                if (child_6.children) {
                                                    child_6.children.forEach(child_8 => {
                                                        if (parent) return;
                                                        if (child_8.value === value) {
                                                            parent = child_6;
                                                        }
                                                    });
                                                }
                                            }
                                        });
                                    }
                                }
                            });
                        }
                    }
                });
                return parent;
            },
            showMajorGroup () {
                this.$modal.show("modal-autocomplete-tree", {
                    title: "Major Industry Group",
                    item: this.item,
                    selected: this.selected
                });

                this.formatValues = [];
                this.search = "";
            },
            refreshMajor () {
                this.formatValues = [
                    this.$refs["major-groups"].initializeLoading()
                ];
                this.$refs["major-groups"].handleAsyncLoad(this.formatValues, this.$refs["major-groups"]);
                this.loading = false;
            },
            toggleAllSelected () {
                this.countShowSelected = this.countShowSelected === this.selected.length ? 5 : this.selected.length;
            }
        },
        data () {
            return {
                search: "",
                perPage: 500,
                countShowSelected: 5,
                limitShowSelected: 5,
                selected: [],
                treeSelected: [],
                searchFocus: false,
                treeMode: false,
                mark: null,
                loading: false,
                formatValues: [],
                loadData: (oriNode, resolve) => {
                    this.loading = true;
                    let defaultData = [
                        {
                            title: "01-09 - Agriculture, Forestry, & Fishing",
                            value: "01,02,07,08,09",
                            level: 1
                        },
                        {
                            title: "10-14 - Mining",
                            value: "10,12,13,14",
                            level: 1
                        },
                        {
                            title: "15-17 - Construction",
                            value: "15,16,17",
                            level: 1
                        },
                        {
                            title: "20-39 - Manufacturing",
                            value: "20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39",
                            level: 1
                        },
                        {
                            title: "40-49 - Transportation",
                            value: "40,41,42,43,44,45,46,47,48,49",
                            level: 1
                        },
                        {
                            title: "50-51 - Wholesale / Distributors",
                            value: "50,51",
                            level: 1
                        },
                        {
                            title: "52-59 - Retail Trade",
                            value: "52,53,54,55,56,57,58,59",
                            level: 1
                        },
                        {
                            title: "60-67 - Finance, Insurance, & Real Estate",
                            value: "60,61,62,63,64,65,66,67",
                            level: 1
                        },
                        {
                            title: "70-89 - Services",
                            value: "70,72,73,75,76,78,79,80,81,82,83,84,86,87,89",
                            level: 1
                        },
                        {
                            title: "91-98 - Public Administration",
                            value: "91,92,93,94,95,96,97",
                            level: 1
                        },
                        {
                            title: "99-99 - Nonclassified Establishments",
                            value: "99",
                            level: 1
                        }
                    ];

                    if (oriNode.data.length) {
                        setTimeout(() => {
                            resolve(defaultData);

                            this.loading = false;
                        }, 50);
                    } else if (oriNode.data.level) {
                        this.$store.dispatch("widgetData/getTreeOneLevel", {
                            code: this.item.code,
                            params: {
                                per_page: this.perPage,
                                query: oriNode.data.value,
                                major: 1,
                                grouped: 1,
                                level: Number(oriNode.data.level) + 1
                            }
                        }).then(({ data: { data: response } }) => {
                            let data = response.map(item => {
                                let hasChild = item.child_count > 0;
                                let findIndex = this.selected.findIndex(itemChild => itemChild.value === item.value);
                                let selected = oriNode.data.selected || findIndex !== -1;
                                return {
                                    ...item,
                                    opened: false,
                                    loading: false,
                                    isLeaf: hasChild,
                                    selected,
                                    level: Number(oriNode.data.level) + 1,
                                    children: hasChild ? [
                                        {
                                            title: "Loading...",
                                            value: "Loading...",
                                            opened: false,
                                            selected: false,
                                            disabled: true,
                                            loading: true,
                                            children: []
                                        }
                                    ] : []
                                };
                            });

                            setTimeout(() => {
                                resolve(data);

                                this.loading = false;
                            }, 50);
                        });
                    }
                }
            };
        },
        mounted () {
            this.mark = new Mark("#mark-highlighter");

            EventBus.$on("remove-select", code => {
                if (code === this.item.code) {
                    this.clearSearch();
                    this.selected = [];
                    this.$emit("close");
                }
            });

            EventBus.$on("clear-all", () => {
                this.clearSearch();
                this.selected = [];
                this.$emit("close");
            });

            EventBus.$on("edit-select", code => {
                if (code === this.item.code && this.$refs[`autocomplete-input-${this.item.code}`]) {
                    setTimeout(() => {
                        let offset = this.$refs[`autocomplete-input-${this.item.code}`].getBoundingClientRect().top + window.scrollY;
                        this.$scrollTo("body", 500, { offset: offset - 130 });
                    }, 50);
                }
            });
        },
        created () {
            if (this.majorGroup) {
                this.selected = [...this.preSelected];
                this.treeMode = true;
            }

            this.checkStoreSelect();
        }
    };
</script>
